import React, { useState } from "react";
import * as yup from "yup";
import { InferType } from "yup";
import {
  AccountDetailFragment,
  ProvisioningAccountIn,
  useProvisioningAccountsQuery,
  useUpdateProvisioningAccountMutation,
  useConfigurationQuery
} from "generated/graphql";
import i18n, { useTranslation } from "utilities/i18n";
import { copyToClipboard } from "utilities/clipboard";
import { Layout } from "views/Main/Layouts";
import {
  Loader,
  PolyIconButton,
  LoadingIndicator,
  CancelButton,
  SubmitButton
} from "elements";
import { DISMISS, TypesafeFieldProps, useNotification } from "collections";
import { Input, Formik, Form } from "collections/Form";
import { useModalDispatch } from "collections/Modal";
import { Route, Link, useHistory, useLocation } from "polyrouter";
import { noOpNotifyContext } from "polygraphql";
import { DisableProvisioningModal } from "./DisableProvisioningModal";
import { EditServerCredentialsModal } from "./EditServerCredentialsModal";
import { ProvisioningGuideModal } from "./ProvisioningGuideModal";

export type ProvisioningAccount = AccountDetailFragment;

const PROV_ACCOUNT_MAX_LENGTH = 80;
const ACCOUNT_NAME_REGEX = /^[a-zA-Z0-9#.\-_()[\]]+$/;
const PASSWORD_REGEX = /^[^\s]+(\s+[^\s]+)*$/;

const userName = yup
  .string()
  .required()
  .max(PROV_ACCOUNT_MAX_LENGTH)
  .matches(ACCOUNT_NAME_REGEX)
  .label(i18n.t("SERVER_USER"));

const password = yup
  .string()
  .max(PROV_ACCOUNT_MAX_LENGTH)
  .required()
  .matches(PASSWORD_REGEX);

const confirmPassword = yup
  .string()
  .required()
  .test("passwordMatch", i18n.t("FORM_VALIDATE_PASSWORD_MISMATCH"), function(
    value
  ) {
    return value === this.parent.password;
  });

const NewProvisioningAccountSchema = yup.object({
  username: userName,
  password: password.label(i18n.t("NEW_PASSWORD")),
  confirmPassword: confirmPassword.label(i18n.t("CONFIRM_PASSWORD"))
});

export const UpdateProvisioningAccountSchema = yup.object({
  username: userName,
  password: password.label(i18n.t("CREATE_PASSWORD")),
  confirmPassword: confirmPassword.label(i18n.t("CONFIRM_NEW_PASSWORD"))
});

export type ProvisioningAccountForm = InferType<
  typeof UpdateProvisioningAccountSchema
>;

export type SubmitFn = (
  values: ProvisioningAccountForm,
  enabled: boolean,
  successMessage: string,
  onSuccess?: () => void
) => any;

const ProvisioningSettingsViewLoader: React.FC<{ id: string }> = ({ id }) => {
  const { data, fetching, error } = useProvisioningAccountsQuery({
    variables: { tenantUuid: id },
    requestPolicy: "network-only",
    context: noOpNotifyContext
  });

  const syncInProgress = !!error?.graphQLErrors.some(
    gqe => gqe?.extensions?.code === "UNKNOWN_TENANT"
  );

  if (error && !syncInProgress) {
    return <></>;
  }

  let account: ProvisioningAccount = {} as ProvisioningAccount;
  if (data?.getProvisioningAccounts && data.getProvisioningAccounts[0]) {
    account = data.getProvisioningAccounts[0];
  }

  return (
    <ProvisioningSettingsView
      tid={id}
      account={account}
      sync={syncInProgress}
      loading={fetching}
    />
  );
};

const ProvisioningSettingsView: React.FC<{
  tid: string;
  account: ProvisioningAccount;
  sync: boolean;
  loading: boolean;
}> = ({ tid, account, sync, loading }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();

  const [, updateAccountMutation] = useUpdateProvisioningAccountMutation();

  const [guideTitleSwitch, setTitleSwitch] = useState(false);

  const submit: SubmitFn = async (
    values,
    enabled,
    successMessage,
    onSuccess
  ) => {
    const { uuid, username, password, description, type } = account;

    const { username: newUsername, password: newPassword } = values;
    const accountInput: ProvisioningAccountIn = {
      uuid,
      username: newUsername || username,
      password: newPassword || password,
      description,
      disabled: !enabled,
      type
    };

    const { data, error } = await updateAccountMutation(
      {
        tenantUuid: tid,
        account: accountInput
      },
      {
        notify: {
          i18nKey: "ERROR_UPDATING_PROVISIONING_SETTINGS"
        }
      }
    );

    if (!error && data?.updateProvisioningAccount) {
      if (enabled) {
        setTitleSwitch(true);
      }

      onSuccess && onSuccess();

      // we need to wrap addNotification in setTimeout to make sure it will always appear
      setTimeout(() =>
        addNotification({
          id: "edit-success",
          type: "success",
          message: successMessage,
          dismiss: DISMISS.DEFAULT
        })
      );
    }
  };

  const { Content } = Layout;

  return (
    <Content className="provisioning-settings-content">
      <Loader loading={loading}>
        <div>
          <div className="card title-card">{t("PROVISIONING")}</div>
          {sync ? (
            <SyncMessage />
          ) : (
            <>
              <div className="mt-15 ph-3">
                <p>{t("PROVISIONING_INFO_1")}</p>
                <p>{t("PROVISIONING_INFO_2")}</p>
                <p>
                  <Link
                    nest
                    to="/gettingstarted"
                    onClick={() => {
                      setTitleSwitch(false);
                    }}
                  >
                    {t("PROVISIONING_GUIDE_LINK")}
                  </Link>
                </p>
              </div>
              <div className="provisioning-details mt-3 pl-3">
                {!account.username ? (
                  <ProvisioningForm submit={submit} />
                ) : (
                  <ProvisioningDetails account={account} submit={submit} />
                )}
                <Route
                  nest
                  path="/gettingstarted"
                  render={() => (
                    <ProvisioningGuideModal isEnabled={guideTitleSwitch} />
                  )}
                />
              </div>
              <div>
                {account.username && (
                  <div className="mt-15 ph-3">
                    <p>{t("PROVISIONING_INFO_3")}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Loader>
    </Content>
  );
};

// TODO: This component (nearly) exists already elsewhere - consider importing it / using it
const ProvField: React.FC<{
  f: TypesafeFieldProps;
  showLabel?: boolean;
  type?: string;
  placeholder?: string;
  tabIndex?: number;
}> = ({
  f: { name, label, ...rest },
  showLabel,
  type = "text",
  placeholder,
  tabIndex,
  children
}) => {
  const inputClass = showLabel ? "col-4" : "col-10";

  return (
    <div className="form-group">
      {showLabel && (
        <label className="col-3 text-right" htmlFor={name}>
          {label}:
        </label>
      )}
      <div className={inputClass}>
        <Input
          type={type}
          name={name}
          {...rest}
          label={showLabel ? "" : label}
          placeholder={placeholder}
          tabIndex={tabIndex}
        />
      </div>
      {children}
    </div>
  );
};

const ProvPasswordField: React.FC<{
  f: TypesafeFieldProps;
  showLabel?: boolean;
  placeholder?: string;
  tabIndex?: number;
}> = ({ f, showLabel, placeholder, tabIndex }) => {
  const [showPwd, setShowPwd] = useState(false);

  return (
    <ProvField
      f={f}
      showLabel={showLabel}
      placeholder={placeholder}
      tabIndex={tabIndex}
      type={!showPwd ? "password" : "text"}
    >
      <div className={showLabel ? "pt-5" : "pt-15"}>
        <PolyIconButton
          type="button"
          className="clickable"
          icon={showPwd ? "eye_slash" : "eye"}
          onClick={() => (!showPwd ? setShowPwd(true) : setShowPwd(false))}
        />
      </div>
    </ProvField>
  );
};

export const ProvisioningFormContent: React.FC<{
  disabled: boolean;
  handleReset: (e?: React.SyntheticEvent<any, Event> | undefined) => void;
  field: any;
  isNew?: boolean;
  isSubmitting: boolean;
  onReset?: () => void;
}> = ({ disabled, handleReset, field, isNew, isSubmitting, onReset }) => {
  const { t } = useTranslation();
  return (
    <Form className="form grid" data-testid="ProvisioningAccountRaw">
      <ProvField
        f={field.username}
        showLabel={isNew}
        placeholder={t("USERNAME")}
        tabIndex={1}
      />
      <ProvPasswordField f={field.password} showLabel={isNew} tabIndex={2} />
      <ProvPasswordField
        f={field.confirmPassword}
        showLabel={isNew}
        tabIndex={3}
      />
      <div className="text-right">
        {!isNew && (
          <CancelButton
            disabled={isSubmitting}
            onClick={e => {
              onReset && onReset();
              handleReset(e);
            }}
          />
        )}
        <SubmitButton
          isSubmitting={isSubmitting}
          disabled={disabled}
          submittingLabelKey={isNew ? "ENABLING" : "UPDATING"}
          labelKey={isNew ? "ENABLE_PROVISIONING" : "UPDATE"}
        />
      </div>
    </Form>
  );
};
export const ProvisioningForm: React.FC<{
  submit: SubmitFn;
}> = ({ submit }) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const location = useLocation();

  return (
    <Formik
      name="ProvisioningAccount"
      initialValues={{
        username: "",
        password: "",
        confirmPassword: ""
      }}
      enableReinitialize
      validationSchema={NewProvisioningAccountSchema}
      onSubmit={values =>
        submit(values, true, t("PROVISIONING_ENABLED"), () =>
          push(location.pathname + "/gettingstarted" + location.search)
        )
      }
    >
      {({ handleReset, isSubmitting, dirty, isValid, field }) => {
        const disabled = isSubmitting || !dirty || !isValid;
        return (
          <ProvisioningFormContent
            disabled={disabled}
            handleReset={handleReset}
            field={field}
            isSubmitting={isSubmitting}
            isNew={true}
          />
        );
      }}
    </Formik>
  );
};

const ProvisioningDetails: React.FC<{
  account: ProvisioningAccount;
  submit: SubmitFn;
}> = ({ account, submit }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const { push } = useHistory();
  const location = useLocation();
  const dispatchEvent = useModalDispatch();
  // Not part of the Formik, so we need manual way of keeping submit state
  // TODO: Consider adding Formik here as well?
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const enable = async (value: boolean) => {
    setIsSubmitting(true);
    const onSuccess = value
      ? () => push(location.pathname + "/gettingstarted" + location.search)
      : () => dispatchEvent();
    await submit(
      {} as ProvisioningAccountForm,
      value,
      t(value ? "PROVISIONING_ENABLED" : "PROVISIONING_DISABLED"),
      onSuccess
    );
    setIsSubmitting(false);
    //Hide password after disabled
    if (!value) {
      setShowPwd(false);
    }
  };

  const copy = async (str: string) => {
    setShowPwd(true);
    const success = await copyToClipboard(str);
    if (success) {
      addNotification({
        id: "copy-success",
        type: "success",
        message: t("PASSWORD_COPIED_TO_CLIPBOARD"),
        dismiss: DISMISS.FAST
      });
    }
  };

  const hidden = <>&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;</>;

  return (
    <div className="grid form">
      <div className={`${account.isDisabled ? "dimmed" : ""}`}>
        <div className="form-group">
          <label className="col-3 text-right" htmlFor="provisioningAddress">
            {t("PROVISIONING_SERVER_ADDRESS")}:
          </label>
          <div className="col-6 col-xl-9 pb-0">
            <span style={{ wordWrap: "break-word" }}>
              {account.provisioningUri}
            </span>
            <span className="pl-1 d-none d-inline-xl">
              <ClipboardIcon account={account} />
            </span>
          </div>
          <div className="col-1 d-none-xl">
            <ClipboardIcon account={account} />
          </div>
        </div>
        <div className="form-group">
          <label className="col-3 text-right">{t("SERVER_USER")}:</label>
          <div className="col-2">{account.username}</div>
        </div>
        <div className="form-group">
          <label className="col-3 text-right">{t("SERVER_PASSWORD")}:</label>
          <div className="col-3">
            {showPwd ? <span>{account.password}</span> : <span>{hidden}</span>}
            <span className="pl-1">
              <PolyIconButton
                className="clickable v-middle"
                icon={showPwd ? "eye_slash" : "eye"}
                disabled={account.isDisabled || !account.provisioningUri}
                onClick={() =>
                  !showPwd ? copy(account.password ?? "") : setShowPwd(false)
                }
              />
            </span>
          </div>
        </div>
      </div>
      <div className="text-right">
        <Link
          nest
          to="/edit-credentials"
          data-testid="edit-credentials"
          className={`btn btn-secondary ${
            account.isDisabled ? "disabled" : ""
          }`}
          onClick={e => {
            if (account.isDisabled) {
              e.preventDefault();
            }
          }}
        >
          {t("EDIT_SERVER_CREDENTIALS")}
        </Link>
        {account.isDisabled ? (
          <SubmitButton
            isSubmitting={isSubmitting}
            onClick={() => enable(true)}
            testId="enable-provisioning"
            labelKey="ENABLE_PROVISIONING"
            submittingLabelKey="ENABLING"
          />
        ) : (
          <Link
            nest
            to="/disable"
            className="btn"
            data-testid="disable-provisioning"
          >
            {t("DISABLE_PROVISIONING")}
          </Link>
        )}
      </div>
      <Route
        nest
        path="/disable"
        render={() => (
          <DisableProvisioningModal
            isSubmitting={isSubmitting}
            onDisable={() => enable(false)}
          />
        )}
      />
      <Route
        nest
        path="/edit-credentials"
        render={() => (
          <EditServerCredentialsModal account={account} submit={submit} />
        )}
      />
    </div>
  );
};

const ClipboardIcon: React.FC<{
  account: ProvisioningAccount;
}> = ({ account }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();

  const copy = async (str: string) => {
    const success = await copyToClipboard(str);
    if (success) {
      addNotification({
        id: "copy-success",
        type: "success",
        message: t("LINK_COPIED_TO_CLIPBOARD"),
        dismiss: DISMISS.FAST
      });
    }
  };

  return (
    <PolyIconButton
      style={{ verticalAlign: "middle", cursor: "pointer" }}
      icon="clipboard"
      disabled={account.isDisabled || !account.provisioningUri}
      onClick={() => copy(account.provisioningUri ?? "")}
      data-testid="copy-to-clipboard"
    />
  );
};

const SyncMessage: React.FC = () => {
  const { t } = useTranslation();
  const { data, fetching } = useConfigurationQuery();

  if (!fetching && data?.getConfiguration) {
    const syncPeriod = data.getConfiguration.tenantSyncPeriod;
    return (
      <div className="mt-3 pt-3 text-center">
        {t("ACCOUNT_SYNC_IN_PROGRESS")}
        <br />
        {t("PROVISIONING_AVAILABLE_WITHIN")}
        <strong>{t("MINUTES_period", { period: syncPeriod })}</strong>
      </div>
    );
  } else if (fetching) {
    return <LoadingIndicator />;
  } else {
    return null;
  }
};

export default ProvisioningSettingsViewLoader;
